import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { refreshSession } from '@/api/baseQuery';
import { selectAuthStatus } from '@/features/auth/authSlice';

/**
 * Restores a session on first load.
 *
 * <p>The access token is memory-only, so a page reload starts with none. The refresh cookie
 * survives the reload though, so one {@code /auth/refresh} at startup either re-establishes the
 * session or resolves it to anonymous — moving auth status out of {@code 'unknown'}, which is
 * what {@link ProtectedRoute} waits on before deciding whether to redirect.
 *
 * <p>The ref guard is load-bearing under React StrictMode, which runs effects twice in dev.
 * A second refresh would present the token the first one already rotated and trip the backend's
 * reuse detection, revoking the whole family — so the attempt must fire exactly once.
 *
 * <p>Skipped on the customer's order chat, which has no session to restore: a customer's
 * browser has no business calling the vendor sign-in endpoints.
 */
export function AuthBootstrap({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectAuthStatus);
  const started = useRef(false);
  const isPublic = useLocation().pathname.startsWith('/order-chat/');

  useEffect(() => {
    if (!started.current && !isPublic && status === 'unknown') {
      started.current = true;
      void refreshSession(dispatch);
    }
  }, [dispatch, status, isPublic]);

  return <>{children}</>;
}
